import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Avatar,
  Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  QrCode2 as QrCodeIcon,
  PendingActions as PendingIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const adminCards = [
    {
      title: 'Comptes en attente',
      description: 'Gérer les demandes d\'inscription',
      icon: <PendingIcon sx={{ fontSize: 48 }} />,
      color: '#ff9800',
      path: '/admin/pending-users',
    },
    {
      title: 'Gestion des utilisateurs',
      description: 'Voir et gérer tous les utilisateurs',
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      color: '#2196f3',
      path: '/admin/users',
    },
    {
      title: 'Tous les QR codes',
      description: 'Vue globale de tous les QR codes et statistiques',
      icon: <QrCodeIcon sx={{ fontSize: 48 }} />,
      color: '#4caf50',
      path: '/admin/qrcodes',
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'secondary.main',
                    width: 56,
                    height: 56,
                  }}
                >
                  <AdminIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                    Administration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bienvenue, {user?.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/dashboard')}
              >
                Retour au tableau de bord
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Admin Cards */}
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Gestion de la plateforme
        </Typography>

        <Grid container spacing={3}>
          {adminCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.path}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8,
                  },
                }}
              >
                <CardActionArea onClick={() => navigate(card.path)} sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: card.color,
                        mb: 2,
                      }}
                    >
                      {React.cloneElement(card.icon, { sx: { fontSize: 48, color: 'white' } })}
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Info Section */}
        <Paper elevation={3} sx={{ p: 3, mt: 4, bgcolor: 'info.light' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon /> Fonctionnalités administrateur
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            En tant qu'administrateur, vous avez accès à toutes les fonctionnalités de la plateforme :
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Comptes en attente</strong> : Approuver ou rejeter les nouvelles inscriptions
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>Gestion utilisateurs</strong> : Promouvoir/rétrograder les rôles, voir tous les comptes
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                <strong>QR codes globaux</strong> : Vue d'ensemble de tous les QR codes avec statistiques détaillées
              </Typography>
            </li>
          </ul>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminDashboard;
